/* src/sim/incomeInterpolator.ts — smooth cash between polls (TASKS.md 2.6, PLAN.md Part 2 §6).
 *
 * The server owns money; this file owns the *illusion of continuity* between the moments the
 * server tells us about it. Polls land every few seconds, so a HUD bound directly to `cash`
 * would sit frozen and then jump — which reads as a stutter, and worse, hides the connection
 * between a price change and its effect.
 *
 * Nothing here is authoritative. It extrapolates from two server-supplied numbers (`cash` and
 * `income.cashPerMin`) and is overwritten by the next poll. It never decides what the player
 * has; it only decides what to draw in the gap (CLAUDE.md non-negotiable #2).
 *
 * The projection uses `cashPerMin`, not `netPerMin`. Since 0017 those are different numbers:
 * net is *profit*, and cash moves by takings, because the ingredients were already paid for at
 * the market. Extrapolating the balance with the profit rate would under-count for the whole
 * poll interval and hand the drift term a correction to absorb every time. `netPerMin` is kept
 * alongside because it is what the HUD *shows* — profit is the number worth reading.
 */

import type { GameState, Money } from '../core/types.ts';

/** Straight-line extrapolation: cash plus the cash rate times elapsed minutes. */
export function projectCash(
  baseCash: Money,
  baseAtMs: number,
  cashPerMin: Money,
  nowMs: number,
): Money {
  const minutes = (nowMs - baseAtMs) / 60_000;
  return (baseCash + cashPerMin * minutes) as Money;
}

/**
 * How long a correction takes to be absorbed, ms. When a poll lands, the true balance rarely
 * matches what was on screen — the rate changed mid-interval, or a mutation moved money. That
 * error is eased out over this window instead of being snapped, so the digits never jump
 * backwards. Short enough to converge well inside one poll interval.
 */
const RECONCILE_MS = 900;

/**
 * Tracks the last server truth and renders a continuous value from it.
 *
 * The trick is `drift`: at every sync we record how far the displayed value was from the
 * server's, then decay that error to zero. The displayed number therefore always *starts* from
 * exactly where it was — continuity is guaranteed by construction rather than by hoping the
 * numbers happen to line up — and converges on the truth within a second.
 */
export class IncomeInterpolator {
  #baseCash: Money = 0 as Money;
  /** What the balance does — used for the projection. */
  #cashPerMin: Money = 0 as Money;
  /** What the player is shown — profit. Never drives the projection. */
  #netPerMin: Money = 0 as Money;
  #baseAtMs = 0;
  #drift = 0;
  #driftAtMs = 0;
  #primed = false;

  /**
   * Adopt a fresh server state.
   *
   * `nowMs` is the client clock. The server's own timestamp is deliberately *not* used as the
   * base: device clocks are routinely minutes off, and a skewed base would make the first
   * frame after every poll leap by that skew. Latency instead costs us at most one round-trip
   * of under-counting, which the drift term then absorbs.
   */
  sync(state: GameState, nowMs: number = Date.now()): void {
    const displayed = this.#primed ? this.value(nowMs) : state.cash;
    this.#baseCash = state.cash;
    this.#cashPerMin = state.income.cashPerMin;
    this.#netPerMin = state.income.netPerMin;
    this.#baseAtMs = nowMs;
    this.#drift = displayed - state.cash;
    this.#driftAtMs = nowMs;
    this.#primed = true;
  }

  /** The value to draw right now: the projection, plus whatever correction is still easing. */
  value(nowMs: number = Date.now()): Money {
    if (!this.#primed) return 0 as Money;
    const projected = projectCash(this.#baseCash, this.#baseAtMs, this.#cashPerMin, nowMs);
    if (this.#drift === 0) return projected;

    const t = (nowMs - this.#driftAtMs) / RECONCILE_MS;
    if (t >= 1) {
      this.#drift = 0;
      return projected;
    }
    /*
     * Smoothstep, not a linear or ease-out ramp. What matters perceptually is that the
     * correction has zero velocity at *both* ends: it begins moving from a standstill at the
     * instant of the poll and arrives at the truth without a stop. An ease-out curve dumps its
     * steepest motion into the first frame after the poll — the most visible moment — which is
     * the flinch this class exists to avoid.
     */
    const remaining = this.#drift * (1 - t * t * (3 - 2 * t));
    return (projected + remaining) as Money;
  }

  /** Profit per minute — what the HUD shows beside the balance. */
  get netPerMin(): Money {
    return this.#netPerMin;
  }

  /** The rate the balance is being extrapolated at. Takings, not profit. */
  get cashPerMin(): Money {
    return this.#cashPerMin;
  }

  get primed(): boolean {
    return this.#primed;
  }

  reset(): void {
    this.#primed = false;
    this.#drift = 0;
    this.#baseCash = 0 as Money;
    this.#cashPerMin = 0 as Money;
    this.#netPerMin = 0 as Money;
  }
}

/** The one interpolator the HUD reads. Fed by net/pollingScheduler.ts. */
export const incomeInterpolator = new IncomeInterpolator();
